import {
  Component,
  OutputEmitterRef,
  Signal,
  output,
  effect,
  viewChild,
  Renderer2,
  InputSignal,
  input,
  ChangeDetectionStrategy,
  ElementRef,
  HostListener,
  computed,
  viewChildren,
  AfterViewInit,
  inject,
} from '@angular/core';
import { SegmentButtonComponent } from './segment-button/segment-button.component';

import { SkeletonTextComponent } from '../skeleton-text/skeleton-text.component';

export interface SegmentOptions {
  value: unknown;
  label: string;
}

@Component({
  selector: 'lyceo-segment',
  templateUrl: './segment.component.html',
  styleUrl: './segment.component.scss',
  imports: [SegmentButtonComponent, SkeletonTextComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegmentComponent implements AfterViewInit {
  private readonly render: Renderer2 = inject(Renderer2);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public readonly selected: OutputEmitterRef<any> = output();
  public options: InputSignal<SegmentOptions[]> = input.required();
  public active: InputSignal<unknown> = input();
  public loading: InputSignal<boolean> = input(false);
  public title: InputSignal<string | undefined> = input();

  protected segments: Signal<readonly SegmentButtonComponent[]> = viewChildren(
    SegmentButtonComponent,
  );

  protected firstLabel: Signal<string> = computed(() => {
    return this.title() ?? (this.options()[0]?.label as string);
  });

  private readonly line: Signal<ElementRef<HTMLDivElement> | undefined> =
    viewChild('line');

  private activeSegment: SegmentButtonComponent | null = null;

  constructor() {
    this.createSelectActiveEffect();
  }

  public ngAfterViewInit(): void {
    this.updateLine();
    this.updateClasses();
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.updateLine();
  }

  protected selectSegment(segment: SegmentButtonComponent): void {
    this.activeSegment = segment;
    this.selected.emit(segment.value());
    // Wait for render tick, in case of size change (e.g. scroll bar).
    setTimeout(() => {
      this.updateLine();
      this.updateClasses();
    });
  }

  private updateLine(): void {
    if (!this.activeSegment || !this.line()) {
      return;
    }

    const el: HTMLElement = this.line()?.nativeElement as HTMLElement;
    this.render.setStyle(el, 'display', 'block');
    this.render.setStyle(
      el,
      'transform',
      'translateX(' + this.activeSegment.x + 'px)',
    );
    this.render.setStyle(el, 'width', this.activeSegment.width + 'px');
  }

  private updateClasses(): void {
    for (const segment of this.segments()) {
      if (segment === this.activeSegment) {
        this.render.addClass(segment.elRef.nativeElement, 'active');
        continue;
      }
      this.render.removeClass(segment.elRef.nativeElement, 'active');
    }
  }

  private createSelectActiveEffect(): void {
    effect(() => {
      if (!this.active() && !this.segments().length) {
        return;
      }

      if (!this.active() && this.segments().length) {
        this.selectSegment(this.segments()[0]);
        return;
      }

      for (const segment of this.segments()) {
        if (segment.value() === this.active()) {
          this.selectSegment(segment);
          break;
        }
      }
    });
  }
}
